import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, Field } from '@promtver/ui-kit';

import { createCheckin, getOfflineVerificationKey, verifyPassAtGate } from '../../api/partner';
import { errorMessage, isNetworkError } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { AppShell } from '../../components/AppShell';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal, formatTime } from '../../lib/datetime';
import { loadOfflineKey, countPendingCheckins, enqueueCheckin, listPendingCheckins, removePendingCheckin, saveOfflineKey, type PendingCheckin } from './offlineQueue';
import { verifyPassOffline } from './offlineVerify';
import { useCamera, type CameraErrorCode } from './useCamera';
import { useQrScanner } from './useQrScanner';

interface ScanOutcome {
  ok: boolean;
  code: string;
  offline: boolean;
  visitor?: string;
  guestCount?: number;
  slotStartsAt?: string;
  slotEndsAt?: string;
  safetyPassed: boolean | null;
  checkedIn: boolean;
  pointsAwarded?: number;
  pendingSync: boolean;
  reason?: string;
  errorText?: string;
}

function newClientEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cameraErrorText(code: CameraErrorCode): string {
  switch (code) {
    case 'no_device':
      return ru.scanner.reasonNoDevice;
    case 'permission_denied':
      return ru.scanner.reasonPermission;
    case 'insecure_context':
      return ru.scanner.reasonInsecure;
    case 'unavailable':
      return ru.scanner.reasonUnavailable;
    default:
      return ru.scanner.reasonUnknown;
  }
}

function reasonText(reason: string | undefined): string {
  switch (reason) {
    case 'already_used':
      return ru.scanner.reasonAlreadyUsed;
    case 'wrong_enterprise':
      return ru.scanner.reasonWrongEnterprise;
    case 'expired':
      return ru.scanner.reasonExpired;
    case 'revoked':
      return ru.scanner.reasonRevoked;
    case 'invalid_signature':
      return ru.scanner.reasonInvalidSignature;
    case 'malformed':
      return ru.scanner.reasonInvalid;
    default:
      return ru.scanner.reasonInvalid;
  }
}

/**
 * Сканер пропусков на проходной (FR-MGR-01):
 * камера смартфона или веб-камера, декодирование QR (jsQR), проверка пропуска
 * сервером либо локально по подписи (офлайн), чек-ин и синхронизация очереди.
 */
export function ScannerPage() {
  const { user } = useAuth();
  const enterpriseId = user?.enterpriseId ?? '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [lastDecodeMs, setLastDecodeMs] = useState(0);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  const scanTimestamps = useRef<number[]>([]);
  const [scansPerMinute, setScansPerMinute] = useState(0);
  const lastCodeRef = useRef<{ code: string; at: number } | null>(null);

  const camera = useCamera(videoRef, cameraOn);

  /* Ключ офлайн-проверки: подтягивается при наличии сети и хранится локально. */
  const offlineKeyQuery = useQuery({
    queryKey: ['partner', 'offline-verification-key'],
    queryFn: getOfflineVerificationKey,
    staleTime: 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (offlineKeyQuery.data) {
      void saveOfflineKey(offlineKeyQuery.data);
    }
  }, [offlineKeyQuery.data]);

  const refreshPending = useCallback(async () => {
    try {
      setPendingCount(await countPendingCheckins());
    } catch {
      setPendingCount(0);
    }
  }, []);

  useEffect(() => {
    void refreshPending();
  }, [refreshPending]);

  /* Синхронизация очереди офлайн-чек-инов при появлении сети (FR-PASS-01). */
  const syncPending = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    let pending: PendingCheckin[] = [];
    try {
      pending = await listPendingCheckins();
    } catch {
      return;
    }
    if (pending.length === 0) {
      await refreshPending();
      return;
    }

    for (const item of pending) {
      try {
        await createCheckin({
          code: item.code,
          source: item.source,
          clientEventId: item.clientEventId,
        });
        await removePendingCheckin(item.clientEventId);
      } catch (error) {
        if (!isNetworkError(error)) {
          await enqueueCheckin({
            ...item,
            attempts: item.attempts + 1,
            lastError: errorMessage(error, ru.common.unknownError),
          });
        }
      }
    }
    await refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      void syncPending();
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const timer = window.setInterval(() => void syncPending(), 30_000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(timer);
    };
  }, [syncPending]);

  /** Офлайн-ветка: подпись проверяется локально, чек-ин ставится в очередь. */
  const handleOffline = useCallback(
    async (raw: string): Promise<ScanOutcome> => {
      const stored = await loadOfflineKey();
      if (!stored) {
        return {
          ok: false,
          code: raw,
          offline: true,
          checkedIn: false,
          pendingSync: false,
          safetyPassed: null,
          reason: 'no_offline_key',
          errorText: ru.scanner.offlineKeyMissing,
        };
      }

      const verification = await verifyPassOffline(raw, stored.keyBase64, enterpriseId);

      if (!verification.ok || !verification.claims) {
        return {
          ok: false,
          code: raw,
          offline: true,
          checkedIn: false,
          pendingSync: false,
          safetyPassed: null,
          reason: verification.reason,
          errorText: reasonText(verification.reason),
        };
      }

      const claims = verification.claims;
      await enqueueCheckin({
        clientEventId: newClientEventId(),
        code: raw,
        source: 'qr',
        createdAt: new Date().toISOString(),
        attempts: 0,
      });
      await refreshPending();

      return {
        ok: true,
        code: raw,
        offline: true,
        visitor: undefined,
        guestCount: claims.guestCount,
        slotStartsAt: claims.startsAt,
        slotEndsAt: claims.endsAt,
        safetyPassed: null,
        checkedIn: true,
        pendingSync: true,
      };
    },
    [enterpriseId, refreshPending],
  );

  const handleCode = useCallback(
    async (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return;

      const now = Date.now();
      const last = lastCodeRef.current;
      // Защита от повторной обработки одного и того же кода в течение 2,5 с.
      if (last && last.code === trimmed && now - last.at < 2500) return;
      lastCodeRef.current = { code: trimmed, at: now };

      scanTimestamps.current = [...scanTimestamps.current, now].filter((value) => now - value < 60_000);
      setScansPerMinute(scanTimestamps.current.length);

      setBusy(true);
      try {
        const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
        if (!isOnline) {
          setOutcome(await handleOffline(trimmed));
          return;
        }

        try {
          const info = await verifyPassAtGate(trimmed);
          if (!info.ok) {
            setOutcome({
              ok: false,
              code: trimmed,
              offline: false,
              checkedIn: false,
              pendingSync: false,
              safetyPassed: info.safetyPassed,
              reason: info.reason,
              errorText: reasonText(info.reason),
            });
            return;
          }

          const checkin = await createCheckin({
            code: trimmed,
            source: 'qr',
            clientEventId: newClientEventId(),
          });

          setOutcome({
            ok: true,
            code: trimmed,
            offline: false,
            visitor: info.visitor,
            guestCount: info.guestCount,
            slotStartsAt: info.slotStartsAt,
            slotEndsAt: info.slotEndsAt,
            safetyPassed: info.safetyPassed,
            checkedIn: checkin.ok,
            pointsAwarded: checkin.pointsAwarded,
            pendingSync: false,
          });
        } catch (error) {
          if (isNetworkError(error)) {
            setOutcome(await handleOffline(trimmed));
            return;
          }
          setOutcome({
            ok: false,
            code: trimmed,
            offline: false,
            checkedIn: false,
            pendingSync: false,
            safetyPassed: null,
            errorText: errorMessage(error, ru.common.unknownError),
          });
        }
      } finally {
        setBusy(false);
      }
    },
    [handleOffline],
  );

  useQrScanner({
    videoRef,
    enabled: cameraOn && camera.state.status === 'active' && !busy,
    onResult: (text) => void handleCode(text),
    onDecodeMs: (milliseconds) => setLastDecodeMs(milliseconds),
  });

  const safetyLabel = useMemo(() => {
    if (outcome?.safetyPassed === true) return ru.scanner.safetyPassed;
    if (outcome?.safetyPassed === false) return ru.scanner.safetyFailed;
    return ru.scanner.safetyUnknown;
  }, [outcome]);

  return (
    <AppShell title={ru.scanner.title} subtitle={ru.scanner.subtitle} active="scanner">
      <Card
        title={ru.scanner.resultTitle}
        action={
          <Badge tone={online ? 'success' : 'warning'}>
            {online ? ru.common.online : ru.common.offline}
          </Badge>
        }
      >
        <div className="relative w-full overflow-hidden rounded-card border border-line bg-ink-900">
          <video
            ref={videoRef}
            className="block aspect-[3/4] w-full object-cover sm:aspect-[4/3]"
            muted
            playsInline
            aria-label={ru.scanner.cameraActive}
          />
          {camera.state.status !== 'active' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink-900/90 p-4 text-center">
              <span className="text-sm text-muted">
                {camera.state.status === 'error' && camera.state.error
                  ? cameraErrorText(camera.state.error)
                  : ru.scanner.cameraStarting}
              </span>
              {camera.state.status === 'error' ? (
                <Button tone="accent" onClick={() => void camera.start()}>
                  {ru.scanner.start}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={() => setCameraOn((value) => !value)}>
            {cameraOn ? ru.scanner.stop : ru.scanner.start}
          </Button>
          {busy ? <Badge tone="steel">{ru.common.loading}</Badge> : null}
        </div>

        <p className="text-xs text-muted">{ru.scanner.hint}</p>

        <div className="grid grid-cols-2 gap-3">
          <KeyValue label={ru.scanner.decodeTime} value={`${Math.round(lastDecodeMs)} мс`} mono />
          <KeyValue label={ru.scanner.perMinute} value={scansPerMinute} mono />
        </div>

        {pendingCount > 0 ? (
          <div className="rounded-card border border-warning/40 bg-warning/10 p-3 text-xs text-content">
            {ru.scanner.pending}: <span className="font-mono">{pendingCount}</span> · {ru.scanner.offlineQueued}
          </div>
        ) : null}
      </Card>

      {outcome ? (
        <Card title={outcome.ok ? ru.scanner.visitor : ru.common.error}>
          <div className="flex flex-col gap-2">
            {outcome.ok ? (
              <>
                <div className="text-lg font-bold">
                  {outcome.visitor ?? ru.common.notSpecified}
                </div>
                <KeyValue label={ru.scanner.guestCount} value={outcome.guestCount ?? '—'} mono />
                <KeyValue
                  label={ru.scanner.slot}
                  value={
                    outcome.slotStartsAt
                      ? `${formatDateTimeLocal(outcome.slotStartsAt)}${
                          outcome.slotEndsAt ? ` — ${formatTime(outcome.slotEndsAt)}` : ''
                        }`
                      : ru.common.notSpecified
                  }
                />
                <KeyValue label={ru.scanner.safety} value={safetyLabel} />
                <KeyValue
                  label={ru.scanner.checkedIn}
                  value={outcome.checkedIn ? ru.common.yes : ru.common.no}
                />
                {typeof outcome.pointsAwarded === 'number' ? (
                  <KeyValue label={ru.scanner.points} value={outcome.pointsAwarded} mono />
                ) : null}
                <KeyValue
                  label={ru.common.status}
                  value={outcome.offline ? ru.scanner.offlineChecked : ru.scanner.onlineDescription}
                />
                {outcome.offline ? (
                  <div className="rounded-card border border-warning/40 bg-warning/10 p-3 text-xs">
                    {ru.scanner.offlineDescription}
                    {outcome.pendingSync ? ` ${ru.scanner.offlineQueued}` : ''}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="text-sm text-danger">{outcome.errorText ?? ru.common.unknownError}</div>
                <KeyValue label={ru.scanner.manualCode} value={outcome.code} mono />
              </>
            )}
          </div>
        </Card>
      ) : null}

      <Card title={ru.scanner.manualCode}>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void handleCode(manualCode);
            setManualCode('');
          }}
        >
          <Field
            label={ru.scanner.manualCode}
            value={manualCode}
            onChange={setManualCode}
            placeholder={ru.scanner.manualPlaceholder}
            autoComplete="off"
          />
          <Button type="submit" tone="accent" disabled={busy || manualCode.trim().length === 0}>
            {ru.scanner.manualSubmit}
          </Button>
        </form>
      </Card>
    </AppShell>
  );
}
